/**
 * @file AbaCaseFinderAlternative.jsx
 * @description Standalone SEO/positioning landing page served at
 *   /aba-case-finder-alternative/. Category-level comparison of ABAcases
 *   against generic ABA job boards / vague case posts — never naming a
 *   specific competitor. Honest about stage: no fake case counts, no
 *   testimonials, no sameAs claims. Visible copy lives in the
 *   `abaCaseFinderAlt.*` i18n namespace (EN + ES, runtime switch only); the
 *   SEO surface (title, description, canonical, JSON-LD) stays English like
 *   the other public pages. The shared provider CTAs (config-aware "Open the
 *   Case Finder" vs the free tracker) use `providerPages.*`. Short sentences,
 *   6th to 8th grade reading level.
 * @module components/AbaCaseFinderAlternative
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, ShieldIcon, BriefcaseIcon,
  XIcon, CheckCircleIcon, ClockIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { usePublicCaseBrowsing } from "../lib/publicBrowsing";
import { afterLink } from "../lib/linkText";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function AbaCaseFinderAlternative({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const isEs = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("es");
  // Admin `public_case_browsing`: OFF = "Open the Case Finder" would land a guest
  // on a signup gate, so the secondary CTA is the free tracker instead.
  const browsingOn = usePublicCaseBrowsing();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The SEO surface (title, description, canonical, JSON-LD) stays English; only the visible copy follows the language switcher.
  usePageSeo({
    title: "ABA Case Finder Alternative to Generic ABA Job Boards | ABAcases",
    description: "Compare ABAcases with generic ABA job boards. Find local ABA cases by pay, schedule, setting, and credential, and track your hours and payments free.",
    canonical: "/aba-case-finder-alternative",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-aba-case-finder-alternative", [
    { name: "Home", url: "/" },
    { name: "ABA Case Finder Alternative", url: "/aba-case-finder-alternative" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as CityRolePage's SeoLink.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goAlerts = () => { setSignupIntent("alerts"); onNavigate("auth"); };
  const goFinder = () => { onNavigate("cases-v2"); };
  const goTracker = () => { onNavigate("free-aba-timesheet-payment-tracker"); };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 980;

  const painPoints = [
    "abaCaseFinderAlt.pain1", "abaCaseFinderAlt.pain2", "abaCaseFinderAlt.pain3", "abaCaseFinderAlt.pain4",
    "abaCaseFinderAlt.pain5", "abaCaseFinderAlt.pain6", "abaCaseFinderAlt.pain7",
  ];

  const fitCards = [
    { Icon: DollarIcon, titleKey: "abaCaseFinderAlt.fit1Title", bodyKey: "abaCaseFinderAlt.fit1Body" },
    { Icon: CalendarIcon, titleKey: "abaCaseFinderAlt.fit2Title", bodyKey: "abaCaseFinderAlt.fit2Body" },
    { Icon: MapPinIcon, titleKey: "abaCaseFinderAlt.fit3Title", bodyKey: "abaCaseFinderAlt.fit3Body" },
    { Icon: ShieldIcon, titleKey: "abaCaseFinderAlt.fit4Title", bodyKey: "abaCaseFinderAlt.fit4Body" },
    { Icon: ClockIcon, titleKey: "abaCaseFinderAlt.fit5Title", bodyKey: "abaCaseFinderAlt.fit5Body" },
  ];
  const genericPoints = ["abaCaseFinderAlt.generic1", "abaCaseFinderAlt.generic2", "abaCaseFinderAlt.generic3", "abaCaseFinderAlt.generic4", "abaCaseFinderAlt.generic5"];
  const goodPoints = ["abaCaseFinderAlt.good1", "abaCaseFinderAlt.good2", "abaCaseFinderAlt.good3", "abaCaseFinderAlt.good4", "abaCaseFinderAlt.good5"];

  return (
    <div lang={isEs ? "es" : "en"} style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t("trackerSeoPages.crumbHome"), onClick: () => onNavigate("home") },
            { label: t("abaCaseFinderAlt.crumbCurrent") },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t("abaCaseFinderAlt.badge")}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>{t("abaCaseFinderAlt.tagline")}</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("abaCaseFinderAlt.h1")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t("abaCaseFinderAlt.heroSub")}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 620, margin: "0 0 28px" }}>
            {t("abaCaseFinderAlt.heroBody")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goAlerts} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: T.shadowIndigo }}>
              {t("abaCaseFinderAlt.ctaAlerts")}
            </button>
            <button onClick={browsingOn ? goFinder : goTracker} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 28px", background: "rgba(255,255,255,0.09)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.28)", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              {browsingOn
                ? <><MapPinIcon size={16} color="#fff" /> {t("providerPages.openCaseFinder", "Open the Case Finder")}</>
                : <><DollarIcon size={16} color="#fff" /> {t("providerPages.trackMyHours", "Track my hours free")}</>}
            </button>
          </div>
        </div>
      </header>

      {/* ── 1. ABA case finder vs. generic job board ─────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.vsTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.vsP1")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.vsP2")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.vsP3")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760 }}>
            {t("abaCaseFinderAlt.vsP4")}
          </p>
        </div>
      </section>

      {/* ── 2. What ABA therapists need before accepting a case ─ */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.needTitle")}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760, marginBottom: 22 }}>
            {t("abaCaseFinderAlt.needIntro")}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
            {painPoints.map((p) => (
              <div key={p} style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px" }}>
                <XIcon size={15} color={T.red} style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.55 }}>{t(p)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Why ABAcases focuses on case fit ─────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.fitTitle")}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760, marginBottom: 24 }}>
            {t("abaCaseFinderAlt.fitIntroBefore")}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.fitIntroLink")}</SeoLink>{afterLink(t("abaCaseFinderAlt.fitIntroAfter"))}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 28 }}>
            {fitCards.map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{t(c.titleKey)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(c.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.honest1")}
          </p>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760, marginBottom: 20 }}>
            {t("abaCaseFinderAlt.honest2")}
          </p>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760 }}>
            {t("abaCaseFinderAlt.citiesBefore")}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.citiesLinkRbtMiami")}</SeoLink>{" "}
            {t("abaCaseFinderAlt.citiesAnd")}{" "}
            <SeoLink to="aba-cases-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.citiesLinkCasesMiami")}</SeoLink>. {t("abaCaseFinderAlt.citiesMid")}{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.citiesLinkRbtCharlotte")}</SeoLink>{" "}
            {t("abaCaseFinderAlt.citiesAnd")}{" "}
            <SeoLink to="aba-cases-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.citiesLinkCasesCharlotte")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 4. Built-in ABA pay and hours tracking ───────────── */}
      <section style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #14193a 100%)`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: INDIGO_LT, fontFamily: T.fontMono, marginBottom: 12 }}>
            {t("abaCaseFinderAlt.trackerEyebrow")}
          </div>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: "#fff", margin: "0 0 14px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.trackerTitle")}
          </h2>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.75)", lineHeight: 1.7, maxWidth: 720, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.trackerP1")}
          </p>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.75)", lineHeight: 1.7, maxWidth: 720, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.trackerP2Before")}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: "#fff", fontWeight: 800, textDecoration: "underline" }}>{t("abaCaseFinderAlt.trackerP2Link")}</SeoLink>{afterLink(t("abaCaseFinderAlt.trackerP2After"))}
          </p>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.75)", lineHeight: 1.7, maxWidth: 720, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.trackerP3")}
          </p>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.75)", lineHeight: 1.7, maxWidth: 720 }}>
            {t("abaCaseFinderAlt.trackerP4Before")}{" "}
            <SeoLink to="rbt-timesheet" style={{ color: "#fff", fontWeight: 700, textDecoration: "underline" }}>{t("abaCaseFinderAlt.trackerP4LinkRbt")}</SeoLink>{" "}
            {t("abaCaseFinderAlt.trackerP4Mid1")}{" "}
            <SeoLink to="aba-timesheet" style={{ color: "#fff", fontWeight: 700, textDecoration: "underline" }}>{t("abaCaseFinderAlt.trackerP4LinkAba")}</SeoLink>{afterLink(t("abaCaseFinderAlt.trackerP4Mid2"))}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: "#fff", fontWeight: 700, textDecoration: "underline" }}>{t("abaCaseFinderAlt.trackerP4LinkPay")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 5. For RBTs, BCBAs, BCaBAs, BTs, and ABA therapists ─ */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.forTitle")}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.forP1")}
          </p>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760 }}>
            {t("abaCaseFinderAlt.forP2")}
          </p>
        </div>
      </section>

      {/* ── 6. For agencies looking to post cases ────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.agTitle")}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 14 }}>
            {t("abaCaseFinderAlt.agP1")}
          </p>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.75, maxWidth: 760, marginBottom: 20 }}>
            {t("abaCaseFinderAlt.agP2")}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
            <SeoLink to="aba-agencies" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: INDIGO }}>
              <BriefcaseIcon size={14} color={INDIGO} /> {t("abaCaseFinderAlt.agLinkAgencies")}
            </SeoLink>
            <SeoLink to="post-aba-cases" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 700, color: INDIGO }}>
              {t("abaCaseFinderAlt.agLinkPost")}
            </SeoLink>
          </div>
          <p style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7, maxWidth: 760, marginTop: 14 }}>
            {t("abaCaseFinderAlt.agAlertsBefore")}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaCaseFinderAlt.agAlertsLink")}</SeoLink>{afterLink(t("abaCaseFinderAlt.agAlertsAfter"))}
          </p>
        </div>
      </section>

      {/* ── Comparison summary ────────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 20 : 25, fontWeight: 800, color: NAVY, margin: "0 0 20px", letterSpacing: "-0.02em" }}>
            {t("abaCaseFinderAlt.glanceTitle")}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16 }}>
            <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "22px 22px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: TEXT_MID, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 14 }}>{t("abaCaseFinderAlt.glanceGenericTitle")}</div>
              {genericPoints.map((x) => (
                <div key={x} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                  <XIcon size={14} color={T.red} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.55 }}>{t(x)}</span>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", border: `1.5px solid ${INDIGO}55`, borderRadius: 14, padding: "22px 22px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: INDIGO, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 14 }}>ABAcases</div>
              {goodPoints.map((x) => (
                <div key={x} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                  <CheckCircleIcon size={14} color={T.green || "#0A6B4E"} style={{ flexShrink: 0, marginTop: 2 }} />
                  <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 600, lineHeight: 1.55 }}>{t(x)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
    </div>
  );
}
